/**
 * CompliancePage — drift / compliance scanning.
 *
 * Surface for the Phase 3 compliance framework:
 *   - List the desired-state presets ported from the legacy
 *     app_workflows/fixtures/initial_data.json (16 of 18 supported today).
 *   - Pick one + a Meraki network from the user's active key.
 *   - Run /api/compliance/scan/ → render the DiagnosisResult: summary KPIs
 *     plus a per-pointer drill-down so the operator can see exactly which
 *     fields drifted and what they should be.
 *
 * Read-only view today — auto-remediation can plug in later by wiring
 * non-compliant rows to existing Meraki write tools.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Download,
  Filter,
  History,
  KeyRound,
  Play,
  Plus,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Trash2,
  Wrench,
  X,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { PanelSkeleton, Skeleton } from "@/components/ui/skeleton";
import { useCompliance } from "@/stores/compliance";
import { useMerakiNetworks } from "@/stores/meraki-networks";
import { useArubaGroups } from "@/stores/aruba-groups";
import { useAuth } from "@/stores/auth";
import type {
  ComplianceResult,
  ComplianceScanSummary,
} from "@/api/schemas/compliance";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/** Lab-safe Aruba group names — drives the Aruba target input's
 *  datalist suggestions + the default when an Aruba preset is picked.
 *  Backend allowlist (`OPUS_ARUBA_COMPLIANCE_REMEDIATE_GROUPS`) gates
 *  live writes to the same set. Update both when the lab footprint
 *  changes. */
const ARUBA_LAB_GROUPS = ["Jedi AI Home", "Jedi AI Office"] as const;

/** Status pill for a single ComplianceResult row. */
function statusBadge(status: ComplianceResult["status"]) {
  if (status === "compliant")
    return (
      <Badge variant="ok" size="sm">
        <CheckCircle2 size={10} />
        compliant
      </Badge>
    );
  if (status === "non-compliant")
    return (
      <Badge variant="err" size="sm">
        <ShieldAlert size={10} />
        drift
      </Badge>
    );
  return (
    <Badge variant="neutral" size="sm">
      ignore
    </Badge>
  );
}

/** Truncate to first N chars of JSON for compact "current/desired" cells. */
/** Searchable single-select dropdown. Replaces native `<select>` when
 *  the option count gets large enough that scrolling is painful (Meraki
 *  network list across all customer orgs is hundreds of entries).
 *
 *  - Click the field to open the list; type to filter (case-insensitive
 *    substring across `label` + `sub`).
 *  - Click an option (or press Enter when there's a single match) to
 *    select; the input shows the chosen label.
 *  - Click outside / blur to close. Mouse-down on the list keeps focus
 *    so options stay clickable. */
function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string; sub?: string }>;
  placeholder?: string;
  disabled?: boolean;
}) {
  const selected = options.find((o) => o.value === value);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  // Display value: when closed, show the selected label; when open,
  // show the in-progress search query.
  const inputValue = open ? search : (selected?.label ?? "");

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sub ? o.sub.toLowerCase().includes(q) : false),
    );
  }, [options, search]);

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={inputValue}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          setSearch("");
          setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered.length === 1) {
            onChange(filtered[0].value);
            setOpen(false);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        aria-label={placeholder}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          "h-9 w-full rounded-md border border-line bg-surface px-2.5 text-sm text-fg",
          "focus:border-violet focus:outline-none disabled:opacity-50",
        )}
      />
      {open && filtered.length > 0 ? (
        <div role="listbox" className="absolute z-20 mt-1 max-h-[280px] w-full overflow-y-auto rounded-md border border-line bg-surface shadow-2xl">
          {filtered.map((o) => {
            const on = o.value === value;
            return (
              <div
                key={o.value}
                role="option"
                aria-selected={on}
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  "cursor-pointer px-2.5 py-1.5 text-sm hover:bg-aurora-soft",
                  on && "bg-aurora-soft text-violet",
                )}
              >
                <div className="font-mono text-[12px] text-fg">{o.label}</div>
                {o.sub ? (
                  <div className="truncate text-[11px] text-fg-mute">
                    {o.sub}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {open && filtered.length === 0 ? (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-[11.5px] text-fg-mute">
          No matches for "{search}"
        </div>
      ) : null}
    </div>
  );
}

function compact(value: unknown, max = 80): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string")
    return value.length > max ? value.slice(0, max - 1) + "…" : value;
  try {
    const s = JSON.stringify(value);
    return s.length > max ? s.slice(0, max - 1) + "…" : s;
  } catch {
    return String(value);
  }
}

function useCompliancePage() {
  const presets = useCompliance((s) => s.presets);
  const presetsStatus = useCompliance((s) => s.presetsStatus);
  const presetsError = useCompliance((s) => s.presetsError);
  const loadPresets = useCompliance((s) => s.loadPresets);
  const report = useCompliance((s) => s.report);
  const scanStatus = useCompliance((s) => s.scanStatus);
  const scanError = useCompliance((s) => s.scanError);
  const runScan = useCompliance((s) => s.runScan);
  const clearReport = useCompliance((s) => s.clearReport);

  const networks = useMerakiNetworks((s) => s.data);
  const networksStatus = useMerakiNetworks((s) => s.status);
  const networksError = useMerakiNetworks((s) => s.error);
  const loadNetworks = useMerakiNetworks((s) => s.load);

  // Aruba groups — pulled on demand for the Aruba-vendor target dropdown.
  const arubaGroups = useArubaGroups((s) => s.data);
  const arubaGroupsStatus = useArubaGroups((s) => s.status);
  const arubaGroupsError = useArubaGroups((s) => s.error);
  const loadArubaGroups = useArubaGroups((s) => s.load);

  // Vendor toggle — drives the preset filter AND the target dropdown.
  // Initial value picks the vendor of the first-available preset on
  // first render so the user lands on a working pair without thinking.
  const [vendorFilter, setVendorFilter] = useState<"meraki" | "aruba">(
    "meraki",
  );

  // Recent-scans rail.
  const history = useCompliance((s) => s.history);
  const historyStatus = useCompliance((s) => s.historyStatus);
  const loadHistory = useCompliance((s) => s.loadHistory);
  const loadScan = useCompliance((s) => s.loadScan);

  // Manual probe trigger.
  const probesRunStatus = useCompliance((s) => s.probesRunStatus);
  const probesRunResult = useCompliance((s) => s.probesRunResult);
  const probesRunError = useCompliance((s) => s.probesRunError);
  const runProbesNow = useCompliance((s) => s.runProbesNow);
  const isAdmin = useAuth((s) => s.user?.roles?.includes("admin")) === true;
  // No-keys gate — compliance scans hit vendor APIs (Meraki for the
  // current MVP, Aruba for the WLAN-security check). A user with no
  // vendor key would 412 on every scan attempt; surface a CTA card
  // before they hit "Run scan" instead of letting them swing at thin
  // air. Mirrors the pattern on /devices and /sites.
  const merakiConfigured = useAuth((s) => s.user?.merakiKeyConfigured) === true;

  // Persisted probes (DB-backed). Admins see the management UI; others
  // just see the count via run-probes-now.
  const setScanTags = useCompliance((s) => s.setScanTags);
  const probes = useCompliance((s) => s.probes);
  // Rail tag filter — multi-select OR semantics. Empty = no filter.
  const [activeRailTags, setActiveRailTags] = useState<Set<string>>(new Set());
  function toggleRailTag(t: string) {
    setActiveRailTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }
  const probesStatus = useCompliance((s) => s.probesStatus);
  const probesError = useCompliance((s) => s.probesError);
  const loadProbes = useCompliance((s) => s.loadProbes);
  const addProbe = useCompliance((s) => s.addProbe);
  const toggleProbe = useCompliance((s) => s.toggleProbe);
  const removeProbe = useCompliance((s) => s.removeProbe);
  const [newProbeSlug, setNewProbeSlug] = useState<string>("");
  const [newProbeTarget, setNewProbeTarget] = useState<string>("");

  // Remediation state — the confirm modal + the queued result.
  const remediationStatus = useCompliance((s) => s.remediationStatus);
  const remediationError = useCompliance((s) => s.remediationError);
  const remediationResult = useCompliance((s) => s.remediationResult);
  const runRemediation = useCompliance((s) => s.runRemediation);
  const clearRemediation = useCompliance((s) => s.clearRemediation);
  const [confirmRemediate, setConfirmRemediate] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    void loadPresets();
    void loadNetworks();
    void loadHistory();
    if (isAdmin) void loadProbes();
  }, [loadPresets, loadNetworks, loadHistory, loadProbes, isAdmin]);

  // Selection state. Default the slug to the first supported preset and
  // the network to whatever name matches "NetEng-Lab" — that's the
  // canonical safe-write/scan target.
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [selectedNetworkId, setSelectedNetworkId] = useState<string>("");
  const [showOnlyDrift, setShowOnlyDrift] = useState(true);
  // Tag filter on the preset picker — narrows the dropdown when the
  // user only wants e.g. firewall presets. Multi-select; empty = all.
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  function changeVendorFilter(next: "meraki" | "aruba") {
    setVendorFilter(next);
    setActiveTags(new Set());
    setSelectedSlug("");
    if (next === "aruba" && arubaGroupsStatus === "idle") {
      void loadArubaGroups();
    }
  }

  function toggleTag(t: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  // Presets scoped to the active vendor — computed once here so both
  // the default-pick logic (below) and the dropdown can use it.
  const vendorScopedPresetsMemo = useMemo(
    () => presets.filter((p) => p.supported && p.vendor === vendorFilter),
    [presets, vendorFilter],
  );

  // Effective slug: user selection when set, otherwise the first available preset.
  const resolvedSlug = selectedSlug || vendorScopedPresetsMemo[0]?.slug || "";

  // Vendor of the currently-selected preset — drives the target picker.
  // Meraki presets get the network dropdown; Aruba presets are tenant-
  // scoped today (single literal target id "tenant").
  const selectedVendor: "meraki" | "aruba" = useMemo(() => {
    const p = presets.find((x) => x.slug === resolvedSlug);
    return (p?.vendor as "meraki" | "aruba" | undefined) ?? "meraki";
  }, [presets, resolvedSlug]);

  // Derive the effective network ID without mutating state: validates/defaults
  // selectedNetworkId based on vendor and loaded data. The raw state is preserved
  // for the dropdown display; resolvedNetworkId is what gets passed to API calls.
  const resolvedNetworkId = useMemo(() => {
    if (selectedVendor === "aruba") {
      const knownAruba =
        selectedNetworkId === "tenant" ||
        (ARUBA_LAB_GROUPS as readonly string[]).includes(selectedNetworkId) ||
        arubaGroups.some((g) => g.name === selectedNetworkId);
      return knownAruba ? selectedNetworkId : ARUBA_LAB_GROUPS[0];
    }
    if (selectedNetworkId === "tenant" && networks.length > 0) {
      const lab = networks.find((n) => n.name.toLowerCase().includes("neteng-lab"));
      return (lab ?? networks[0])!.id;
    }
    if (!selectedNetworkId && networks.length > 0) {
      const lab = networks.find((n) => n.name.toLowerCase().includes("neteng-lab"));
      return (lab ?? networks[0])!.id;
    }
    return selectedNetworkId;
  }, [selectedVendor, selectedNetworkId, networks, arubaGroups]);

  const supportedPresets = useMemo(
    () => presets.filter((p) => p.supported),
    [presets],
  );
  const unsupportedCount = presets.length - supportedPresets.length;

  // Universe of tags — scoped to the vendor's presets so the chips
  // change when you flip vendor.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    for (const p of vendorScopedPresetsMemo) for (const t of p.tags) s.add(t);
    return Array.from(s).sort();
  }, [vendorScopedPresetsMemo]);

  // Apply the tag chips on top of the vendor filter.
  const visiblePresets = useMemo(() => {
    if (activeTags.size === 0) return vendorScopedPresetsMemo;
    return vendorScopedPresetsMemo.filter((p) => {
      const tagSet = new Set(p.tags);
      for (const t of activeTags) if (!tagSet.has(t)) return false;
      return true;
    });
  }, [vendorScopedPresetsMemo, activeTags]);

  // Compute "delta vs previous" — when the active report has a scanId
  // (i.e. it was a real scan, not a saved replay), find the most recent
  // PRIOR scan for the same (network, preset) in `history` and compute
  // {fixed, new, persistent} drift counts. Drives the badge below.
  const delta = useMemo(() => {
    if (!report || !report.scanId) return null;
    // Find the just-saved one + its previous sibling.
    const matching = history.filter(
      (h) =>
        h.networkId === report.target.split(" (").pop()?.replace(/\)$/, "") ||
        h.networkId === report.target,
    );
    // Two ways the target string might be shaped: bare id (from a fresh
    // scan) OR "Name (id)" (from a saved-scan replay). Match either.
    const m2 = history.filter(
      (h) =>
        h.presetSlug === report.presetSlug &&
        (h.networkId === report.target ||
          report.target.endsWith(`(${h.networkId})`)),
    );
    const sameStreak = m2.length > 0 ? m2 : matching;
    const sorted = [...sameStreak].sort((a, b) => (a.ranAt < b.ranAt ? 1 : -1));
    // sorted[0] is the current (just landed); sorted[1] is the previous.
    if (sorted.length < 2) return null;
    const prevId = sorted[1].id;
    // We only have summary counts for the previous in `history`, not the
    // full pointer set, so the delta we can compute without an extra
    // fetch is just the count change. That's still useful at a glance.
    const cur = sorted[0].summary;
    const prev = sorted[1].summary;
    const driftDelta =
      (cur["non-compliant"] ?? 0) - (prev["non-compliant"] ?? 0);
    return { prevId, driftDelta, prev, cur };
  }, [report, history]);

  // Find a baseline-tagged scan for the same (network, preset). When one
  // exists AND it isn't the active scan, surface a "Compare to baseline"
  // button on the drill-in. Tags work, so this is essentially "diff to
  // whichever scan the operator chose to mark as authoritative".
  const baselineScanId = useMemo(() => {
    if (!report) return null;
    // One pass to collect IDs of "baseline"-tagged scans — O(n) up front so
    // the find below can use Set.has() (O(1)) instead of array.includes().
    const baselineIds = new Set<string>();
    for (const h of history) {
      for (const tag of h.tags ?? []) {
        if (tag === "baseline") { baselineIds.add(h.id); break; }
      }
    }
    // Match the current scan's network either via the bare id OR the
    // "Name (id)" replay shape — same gymnastics as the delta calc above.
    const baseline = history.find(
      (h) =>
        h.presetSlug === report.presetSlug &&
        baselineIds.has(h.id) &&
        (h.networkId === report.target ||
          report.target.endsWith(`(${h.networkId})`)) &&
        h.id !== report.scanId,
    );
    return baseline?.id ?? null;
  }, [report, history]);

  // Filter the report rows for the drill-down. With drift-only on (default),
  // only non-compliant entries surface — that's typically what the user
  // came for. Unchecking shows the full tree (useful for "what does
  // 'compliant' look like for this preset?").
  const visibleResults = useMemo(() => {
    if (!report) return [];
    return showOnlyDrift
      ? report.results.filter((r) => r.status === "non-compliant")
      : report.results;
  }, [report, showOnlyDrift]);

  const isInitialLoading =
    presetsStatus === "loading" &&
    networksStatus === "loading" &&
    historyStatus === "loading" &&
    !report;

  return {
    presets, presetsStatus, presetsError, report, scanStatus, scanError,
    runScan, clearReport, networks, networksStatus, networksError,
    arubaGroups, arubaGroupsStatus, arubaGroupsError,
    vendorFilter, setVendorFilter, changeVendorFilter, history, historyStatus, loadHistory, loadScan,
    probesRunStatus, probesRunResult, probesRunError, runProbesNow,
    isAdmin, merakiConfigured, setScanTags, probes,
    activeRailTags, setActiveRailTags, toggleRailTag,
    probesStatus, probesError, loadProbes, addProbe, toggleProbe, removeProbe,
    newProbeSlug, setNewProbeSlug, newProbeTarget, setNewProbeTarget,
    remediationStatus, remediationError, remediationResult, runRemediation, clearRemediation,
    confirmRemediate, setConfirmRemediate, navigate,
    selectedSlug, setSelectedSlug, resolvedSlug, selectedNetworkId, setSelectedNetworkId, resolvedNetworkId,
    showOnlyDrift, setShowOnlyDrift, activeTags, setActiveTags,
    selectedVendor, supportedPresets, unsupportedCount, allTags, visiblePresets,
    delta, baselineScanId, visibleResults, isInitialLoading, toggleTag,
  };
}

type CompliancePageState = ReturnType<typeof useCompliancePage>;

function ComplianceScanCard({ c }: { c: CompliancePageState }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Run a scan</CardTitle>
          <div className="flex items-center gap-2">
            {c.unsupportedCount > 0 ? (
              <Badge variant="neutral" size="sm">
                {c.unsupportedCount} preset{c.unsupportedCount === 1 ? "" : "s"} not yet wired
              </Badge>
            ) : null}
            {c.isAdmin ? (
              <Button size="sm" variant="ghost" onClick={() => void c.runProbesNow()}
                disabled={c.probesRunStatus === "loading"}
                title="Run all probes configured in OPUS_SCHEDULED_PROBES now">
                {c.probesRunStatus === "loading" ? <><RefreshCw size={11} className="animate-spin" /> Running probes…</> : <><Zap size={11} /> Run probes now</>}
              </Button>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {(["meraki", "aruba"] as const).map((v) => {
            const on = c.vendorFilter === v;
            const count = c.supportedPresets.filter((p) => p.vendor === v).length;
            const accent = v === "meraki"
              ? { border: "border-mint", bg: "bg-mint/10", text: "text-mint", label: "Cisco Meraki" }
              : { border: "border-violet", bg: "bg-violet/10", text: "text-violet", label: "HPE Aruba Central" };
            return (
              <button key={v} type="button"
                onClick={() => c.changeVendorFilter(v)}
                className={cn("flex flex-col gap-1 rounded-lg border-2 p-3 text-left transition-all",
                  on ? `${accent.border} ${accent.bg}` : "border-line bg-surface hover:bg-surface-2")}>
                <div className="flex items-center justify-between gap-2">
                  <span className={cn("text-[13px] font-bold uppercase tracking-wide", on ? accent.text : "text-fg-mute")}>{v}</span>
                  <span className={cn("rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold", on ? `${accent.bg} ${accent.text}` : "bg-surface-2 text-fg-mute")}>
                    {count} preset{count === 1 ? "" : "s"}
                  </span>
                </div>
                <span className={cn("text-[11.5px]", on ? "text-fg" : "text-fg-mute")}>{accent.label}</span>
              </button>
            );
          })}
        </div>
        {c.allTags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              <Tag size={10} className="mr-1 inline" />Filter by tag
            </span>
            {c.allTags.map((t) => {
              const on = c.activeTags.has(t);
              return (
                <button key={t} type="button" onClick={() => c.toggleTag(t)}
                  className={cn("rounded-full border px-2 py-0.5 text-[10.5px] font-semibold transition-colors",
                    on ? "border-violet/40 bg-aurora-soft text-violet" : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg")}>{t}</button>
              );
            })}
            {c.activeTags.size > 0 ? (
              <button type="button" onClick={() => c.setActiveTags(new Set())} className="ml-1 text-[10px] text-fg-mute hover:text-fg">clear</button>
            ) : null}
            <span className="ml-auto font-mono text-[10px] text-fg-mute">{c.visiblePresets.length}/{c.supportedPresets.length} presets</span>
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <label htmlFor="compliance-preset" className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">Preset</span>
            <Combobox id="compliance-preset" value={c.resolvedSlug} onChange={c.setSelectedSlug}
              disabled={c.presetsStatus === "loading" || c.scanStatus === "loading"}
              placeholder={`Search ${c.visiblePresets.length} preset${c.visiblePresets.length === 1 ? "" : "s"}…`}
              options={c.visiblePresets.map((p) => ({ value: p.slug, label: p.slug, sub: p.description }))} />
          </label>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="compliance-network" className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              {c.selectedVendor === "aruba" ? "Target" : "Network"}
            </label>
            {c.selectedVendor === "aruba" ? (
              <Combobox id="compliance-network" value={c.resolvedNetworkId} onChange={c.setSelectedNetworkId}
                disabled={c.arubaGroupsStatus === "loading" || c.scanStatus === "loading"}
                placeholder={`Search ${c.arubaGroups.length} Aruba group${c.arubaGroups.length === 1 ? "" : "s"}…`}
                options={[{ value: "tenant", label: "tenant", sub: "read-only, all WLANs tenant-wide" },
                  ...c.arubaGroups.map((g) => ({ value: g.name, label: g.name, sub: "" }))]} />
            ) : (
              <Combobox id="compliance-network" value={c.resolvedNetworkId} onChange={c.setSelectedNetworkId}
                disabled={c.networksStatus === "loading" || c.scanStatus === "loading"}
                placeholder={`Search ${c.networks.length} Meraki network${c.networks.length === 1 ? "" : "s"}…`}
                options={c.networks.map((n) => ({ value: n.id, label: n.name, sub: n.organizationName || "" }))} />
            )}
          </div>
        </div>
        <div className="flex items-center justify-end">
          <Button variant="primary"
            onClick={() => void c.runScan({ networkId: c.resolvedNetworkId, presetSlug: c.resolvedSlug })}
            disabled={!c.resolvedSlug || !c.resolvedNetworkId || c.scanStatus === "loading"}>
            {c.scanStatus === "loading" ? <><RefreshCw size={13} className="animate-spin" /> Scanning…</> : <><Play size={13} /> Run scan</>}
          </Button>
        </div>
        {c.resolvedSlug ? (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-fg-mute">
            <Tag size={11} />
            {(c.presets.find((p) => p.slug === c.resolvedSlug)?.tags ?? []).map((t) => (
              <Badge key={t} variant="neutral" size="sm">{t}</Badge>
            ))}
          </div>
        ) : null}
        {c.scanError ? (
          <output className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose">
            <AlertTriangle size={16} /><span><b className="font-semibold">Scan failed.</b> <span className="text-rose/80">{c.scanError}</span></span>
          </output>
        ) : null}
        {c.probesRunError ? (
          <output className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose">
            <AlertTriangle size={16} /><span><b className="font-semibold">Probe run failed.</b> <span className="text-rose/80">{c.probesRunError}</span></span>
          </output>
        ) : null}
        {c.probesRunResult ? (
          <div className="rounded-xl border border-line bg-surface-tint/40 px-3.5 py-2.5 text-[12px]">
            <div className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
              <Zap size={10} />Manual probe run · {c.probesRunResult.ran} probe{c.probesRunResult.ran === 1 ? "" : "s"}
            </div>
            {c.probesRunResult.ran === 0 ? (
              <div className="text-fg-mute">No probes configured. Set <code className="rounded bg-surface-2 px-1 py-0.5">OPUS_SCHEDULED_PROBES</code> in docker-compose to enable.</div>
            ) : (
              <ul className="m-0 flex flex-col gap-1">
                {c.probesRunResult.results.map((p) => (
                  <li key={`${p.presetSlug}-${p.networkId}`} className={cn("flex items-center gap-2 font-mono text-[11px]",
                    p.status === "drift" ? "text-rose" : p.status === "error" ? "text-peach" : "text-fg-dim")}>
                    <span className="w-2 shrink-0 text-center">{p.status === "drift" ? "✗" : p.status === "error" ? "!" : "✓"}</span>
                    <span className="truncate">{p.presetSlug}</span>
                    <span className="text-fg-mute">→ {p.networkId}</span>
                    {p.reason ? <span className="text-fg-mute">— {p.reason}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ComplianceReportPanel({ c }: { c: CompliancePageState }) {
  if (!c.report) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <ClipboardCheck size={20} className="text-violet" />
          <CardTitle>No scan run yet</CardTitle>
          <CardSub>Pick a preset + network above and click Run scan.</CardSub>
        </CardContent>
      </Card>
    );
  }
  const { report } = c;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile label="Compliant" value={report.summary.compliant} tone="ok" Icon={ShieldCheck} />
        <KpiTile label="Drift" value={report.summary["non-compliant"]} tone={report.summary["non-compliant"] > 0 ? "err" : "neutral"} Icon={ShieldAlert} />
        <KpiTile label="Ignored" value={report.summary.ignore} Icon={Filter} />
        <KpiTile label="Total checks" value={report.results.length} Icon={ClipboardCheck} />
      </div>
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <CardTitle className="text-base">{report.process}</CardTitle>
            <CardSub className="font-mono text-[11px]">
              preset <b className="text-fg">{report.presetSlug}</b> · target <b className="text-fg">{report.target}</b>
              {report.durationMs !== undefined ? <> · ran in <b className="text-fg">{report.durationMs}ms</b></> : null}
            </CardSub>
          </div>
          {c.delta ? <DeltaBadge delta={c.delta.driftDelta} /> : null}
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={() => c.setShowOnlyDrift((v) => !v)}
              className={cn("rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                c.showOnlyDrift ? "border-violet/40 bg-aurora-soft text-violet" : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg")}>
              Drift only · {c.showOnlyDrift ? "on" : "off"}
            </button>
            {c.resolvedSlug && c.resolvedNetworkId ? (
              <Button size="sm" variant="ghost"
                onClick={() => void c.runScan({ networkId: c.resolvedNetworkId, presetSlug: report.presetSlug })}
                disabled={c.scanStatus === "loading"} title="Re-run this scan">
                <RefreshCw size={11} className={c.scanStatus === "loading" ? "animate-spin" : ""} />Re-run
              </Button>
            ) : null}
            {report.scanId && c.baselineScanId ? (
              <Link to={`/compliance/diff/${encodeURIComponent(report.scanId)}/${encodeURIComponent(c.baselineScanId)}`}
                title="Diff this scan against the baseline-tagged run for the same (network, preset)"
                className="inline-flex items-center gap-1 rounded-md border border-violet/40 bg-aurora-soft px-2.5 py-1 text-[12px] font-semibold text-violet hover:brightness-110">
                <Tag size={11} />Compare to baseline
              </Link>
            ) : null}
            {report.scanId ? (
              <a href={`/api/compliance/scans/${encodeURIComponent(report.scanId)}/export.csv`} download
                title="Download this scan as CSV"
                className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2.5 py-1 text-[12px] font-semibold text-fg-mute hover:bg-surface-2 hover:text-fg">
                <Download size={11} />Export CSV
              </a>
            ) : null}
            {report.summary["non-compliant"] > 0 && c.presets.find((p) => p.slug === report.presetSlug)?.remediable && c.resolvedNetworkId ? (
              <Button size="sm" variant="ghost"
                onClick={() => { c.clearRemediation(); c.setConfirmRemediate(true); }}
                title="Push the preset's baseline to this network — replaces current config">
                <Wrench size={11} />Remediate
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => c.clearReport()}>Dismiss</Button>
          </div>
          {report.scanId ? (
            <ScanTagEditor scanId={report.scanId} tags={report.tags ?? []}
              onChange={(next) => void c.setScanTags(report.scanId!, next)} />
          ) : null}
        </CardContent>
      </Card>
      {c.visibleResults.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            {report.summary["non-compliant"] === 0 ? (
              <><ShieldCheck size={28} className="text-mint" /><CardTitle>All clear</CardTitle><CardSub>No drift detected against <b>{report.presetSlug}</b>. The network matches the desired baseline.</CardSub></>
            ) : (
              <><Filter size={20} className="text-fg-mute" /><CardTitle>Filtered out</CardTitle><CardSub>Toggle "Drift only" off to see compliant entries too.</CardSub></>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {c.visibleResults.map((r) => (
            <ResultRow key={r.pointer} result={r} networkId={c.resolvedNetworkId} presetSlug={report.presetSlug} />
          ))}
        </div>
      )}
    </>
  );
}

function ComplianceProbesAdmin({ c }: { c: CompliancePageState }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarClock size={13} className="text-violet" />
            <CardTitle>Scheduled probes</CardTitle>
            {c.probes.length > 0 ? <Badge variant="neutral" size="sm">{c.probes.length}</Badge> : null}
          </div>
          <Button size="sm" variant="ghost" onClick={() => void c.loadProbes()} disabled={c.probesStatus === "loading"}>
            <RefreshCw size={11} className={c.probesStatus === "loading" ? "animate-spin" : ""} />
          </Button>
        </div>
        {c.probesError ? (
          <output className="flex items-center gap-2 rounded-md border border-rose/30 bg-rose/10 px-3 py-2 text-[12px] text-rose">
            <AlertTriangle size={13} /><span>{c.probesError}</span>
          </output>
        ) : null}
        {c.probes.length === 0 ? (
          c.probesStatus === "loading" ? <PanelSkeleton /> : (
            <CardSub>No probes scheduled. Add one below to fire compliance scans every housekeeping tick (default 6h).</CardSub>
          )
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {c.probes.map((p) => {
              const lastFired = c.history.find((h) => h.networkId === p.networkId && h.presetSlug === p.presetSlug && h.source === "scheduled_probe");
              const lastDrift = lastFired?.summary["non-compliant"] ?? null;
              return (
                <li key={p.id} className={cn("flex items-center gap-2 rounded-md border px-2.5 py-1.5", p.enabled ? "border-line bg-surface" : "border-line bg-surface-2 opacity-60")}>
                  <Badge variant={p.vendor === "aruba" ? "info" : "neutral"} size="sm">{p.vendor}</Badge>
                  <span className="font-mono text-[11.5px] text-fg">{p.presetSlug}</span>
                  <span className="text-fg-mute">→</span>
                  <span className="truncate font-mono text-[11.5px] text-fg-dim">{p.networkId}</span>
                  {lastFired ? (
                    <span className={cn("ml-2 inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 font-mono text-[10px]",
                      lastDrift && lastDrift > 0 ? "border-rose/30 bg-rose/5 text-rose" : "border-mint/30 bg-mint/5 text-mint")}
                      title={`last fired ${lastFired.ranAt}; ${lastDrift} drift`}>
                      <Clock size={9} />{formatRelative(lastFired.ranAt)}<span className="text-fg-mute">·</span>
                      {lastDrift && lastDrift > 0 ? `${lastDrift} drift` : "clean"}
                    </span>
                  ) : (
                    <span className="ml-2 font-mono text-[10px] text-fg-mute">never fired</span>
                  )}
                  <span className="ml-auto flex items-center gap-1.5">
                    <button type="button" onClick={() => void c.toggleProbe(p.id, !p.enabled)}
                      className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors",
                        p.enabled ? "border-mint/40 bg-mint/10 text-mint hover:brightness-110" : "border-line bg-surface text-fg-mute hover:bg-surface-2")}
                      title={p.enabled ? "Disable probe (worker skips on next tick)" : "Enable probe"}>
                      {p.enabled ? "enabled" : "disabled"}
                    </button>
                    <button type="button" onClick={() => { if (window.confirm(`Remove probe ${p.presetSlug} → ${p.networkId}?`)) void c.removeProbe(p.id); }}
                      className="rounded-md p-1 text-fg-mute hover:bg-rose/10 hover:text-rose" title="Remove probe">
                      <Trash2 size={11} />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <div className="flex flex-wrap items-end gap-2 border-t border-line pt-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="probe-preset-select" className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">Preset</label>
            <select id="probe-preset-select" value={c.newProbeSlug} onChange={(e) => c.setNewProbeSlug(e.target.value)}
              className="h-8 rounded-md border border-line bg-surface px-2 text-[12px] text-fg focus:border-violet focus:outline-none">
              <option value="">— pick —</option>
              {c.supportedPresets.map((p) => <option key={p.slug} value={p.slug}>{p.slug} ({p.vendor})</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="probe-target" className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">Target</label>
            <input
              id="probe-target"
              value={c.newProbeTarget}
              onChange={(e) => c.setNewProbeTarget(e.target.value)}
              placeholder={
                (c.supportedPresets.find((p) => p.slug === c.newProbeSlug)?.vendor ?? "meraki") === "aruba"
                  ? 'Aruba group, e.g. "Jedi AI Home"'
                  : "Meraki network id, e.g. L_787566984836416999"
              }
              list={
                (c.supportedPresets.find((p) => p.slug === c.newProbeSlug)?.vendor ?? "meraki") === "aruba"
                  ? "aruba-group-suggestions"
                  : undefined
              }
              className="h-8 w-[260px] rounded-md border border-line bg-surface px-2 text-[12px] font-mono text-fg focus:border-violet focus:outline-none"
            />
          </div>
          <Button size="sm" variant="ghost" disabled={!c.newProbeSlug || !c.newProbeTarget}
            onClick={async () => {
              const ok = await c.addProbe({ presetSlug: c.newProbeSlug, networkId: c.newProbeTarget.trim() });
              if (ok) { c.setNewProbeSlug(""); c.setNewProbeTarget(""); }
            }}>
            <Plus size={11} />Add probe
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ComplianceScanRail({ c }: { c: CompliancePageState }) {
  const allRailTags = Array.from(new Set(c.history.flatMap((h) => h.tags ?? []))).sort();
  const filteredHistory = c.activeRailTags.size === 0
    ? c.history
    : c.history.filter((h) => (h.tags ?? []).some((t) => c.activeRailTags.has(t)));
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <History size={13} className="text-violet" />
            <CardTitle>Recent scans</CardTitle>
            {c.history.length > 0 ? <Badge variant="neutral" size="sm">{c.history.length}</Badge> : null}
          </div>
          <Button size="sm" variant="ghost" onClick={() => void c.loadHistory()} disabled={c.historyStatus === "loading"}>
            <RefreshCw size={11} className={c.historyStatus === "loading" ? "animate-spin" : ""} />
          </Button>
        </div>
        {allRailTags.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute"><Tag size={10} className="mr-1 inline" /> Tag</span>
            {allRailTags.map((t) => {
              const on = c.activeRailTags.has(t);
              return (
                <button key={t} type="button" onClick={() => c.toggleRailTag(t)}
                  className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors",
                    on ? "border-violet/40 bg-aurora-soft text-violet" : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg")}>{t}</button>
              );
            })}
            {c.activeRailTags.size > 0 ? (
              <button type="button" onClick={() => c.setActiveRailTags(new Set())} className="ml-1 text-[10px] text-fg-mute hover:text-fg">clear</button>
            ) : null}
          </div>
        ) : null}
        {c.history.length === 0 ? (
          c.historyStatus === "loading" ? <PanelSkeleton /> : <CardSub>No scans yet — run one above.</CardSub>
        ) : filteredHistory.length === 0 ? (
          <CardSub>No scans match the selected tag(s).</CardSub>
        ) : (
          <ul className="m-0 flex max-h-[640px] list-none flex-col gap-1 overflow-y-auto p-0">
            {filteredHistory.map((h) => {
              const fullIdx = c.history.findIndex((x) => x.id === h.id);
              const prev = c.history.slice(fullIdx + 1).find((p) => p.networkId === h.networkId && p.presetSlug === h.presetSlug);
              return <ScanRailRow key={h.id} scan={h} prevScanId={prev?.id ?? null} onLoad={() => void c.loadScan(h.id)} />;
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function CompliancePage() {
  const animatedRootRef = useUiAutoAnimate();
  const c = useCompliancePage();

  if (c.isInitialLoading) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" /><span className="opacity-40">/</span><Skeleton className="h-3 w-24" />
        </div>
        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-44" /><Skeleton className="h-4 w-[92%]" /><Skeleton className="h-4 w-[82%]" />
          </div>
          <Skeleton className="h-9 w-44 rounded-full" />
        </div>
        <PanelSkeleton className="h-[96px]" />
        <PanelSkeleton className="h-[460px]" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <PanelSkeleton className="h-[420px]" /><PanelSkeleton className="h-[420px]" />
        </div>
        <PanelSkeleton className="h-[420px]" />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span><span className="text-fg">Compliance</span>
      </div>
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Compliance</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Drift detection — pick a desired-state preset (from the GuestTek baseline fixture) and a network, and Opus will compare the live config against it field-by-field. Ported from
            <code className="mx-1 rounded bg-surface-2 px-1 py-0.5 font-mono text-[11.5px]">app_workflows/framework</code>.
            {c.supportedPresets.length > 0 ? (
              <> <b className="text-fg">{c.supportedPresets.length}</b> presets wired ({c.supportedPresets.filter((p) => p.remediable).length} remediable).</>
            ) : null}
          </p>
        </div>
        <Badge variant="info" size="md"><ClipboardCheck size={11} />{c.supportedPresets.length} presets supported</Badge>
      </div>

      {!c.merakiConfigured ? (
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-aurora-soft text-violet"><KeyRound size={20} /></div>
            <div className="flex-1">
              <div className="font-semibold text-fg">Add a vendor key to run scans</div>
              <div className="text-sm text-fg-dim">Compliance checks call your vendor's live API. Without a Meraki key (or an Aruba bundle for Aruba presets), every scan returns "no credentials". Configure one to get started.</div>
            </div>
            <div className="flex gap-2">
              <Button asChild size="sm" variant="ghost"><Link to="/aruba/keys"><KeyRound size={13} />Aruba bundle</Link></Button>
              <Button asChild size="sm" variant="primary"><Link to="/meraki/keys"><KeyRound size={13} />Meraki key</Link></Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {c.presetsError ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose">
          <AlertTriangle size={16} /><span><b className="font-semibold">Could not load presets.</b> <span className="text-rose/80">{c.presetsError}</span></span>
        </output>
      ) : null}
      {c.vendorFilter === "meraki" && c.networksStatus === "error" && c.networksError ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span><b className="font-semibold">Meraki networks: showing fixture data.</b> <span className="text-peach/80">{c.networksError}. Picking one of these will 404 on the real Meraki API. Check your active Meraki key on <Link to="/meraki/keys" className="underline">/meraki/keys</Link>.</span></span>
        </output>
      ) : null}
      {c.vendorFilter === "aruba" && c.arubaGroupsStatus === "error" && c.arubaGroupsError ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span><b className="font-semibold">Aruba groups: showing fixture data.</b> <span className="text-peach/80">{c.arubaGroupsError}. Usually means the Aruba refresh token rotated. Regenerate at <Link to="/aruba/keys" className="underline">/aruba/keys</Link>, then reload this page.</span></span>
        </output>
      ) : null}

      <ComplianceScanCard c={c} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-5 min-w-0">
          <ComplianceReportPanel c={c} />
        </div>
        {c.isAdmin ? <ComplianceProbesAdmin c={c} /> : null}
        <ComplianceScanRail c={c} />
      </div>

      {c.confirmRemediate && c.report ? (
        <RemediateModal
          presetSlug={c.report.presetSlug} processName={c.report.process}
          networkId={c.resolvedNetworkId} driftCount={c.report.summary["non-compliant"] ?? 0}
          status={c.remediationStatus} error={c.remediationError} result={c.remediationResult}
          onPreview={() => void c.runRemediation({ networkId: c.resolvedNetworkId, presetSlug: c.report!.presetSlug, dryRun: true })}
          onLive={async () => {
            const runId = await c.runRemediation({ networkId: c.resolvedNetworkId, presetSlug: c.report!.presetSlug, dryRun: false });
            if (runId) { c.setConfirmRemediate(false); c.clearRemediation(); c.navigate(`/workflows?run=${encodeURIComponent(runId)}`); }
          }}
          onClose={() => { c.setConfirmRemediate(false); c.clearRemediation(); }}
        />
      ) : null}
    </div>
  );
}

/** Confirm modal for compliance remediation. Dry-run first → operator
 *  reviews → live push. The Live button stays disabled until the user
 *  has at least previewed once, so a stray click can't push without
 *  seeing the dry-run output. */
function RemediateModal({
  presetSlug,
  processName,
  networkId,
  driftCount,
  status,
  error,
  result,
  onPreview,
  onLive,
  onClose,
}: {
  presetSlug: string;
  processName: string;
  networkId: string;
  driftCount: number;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  result: { id: string; dryRun: boolean } | null;
  onPreview: () => void;
  onLive: () => void;
  onClose: () => void;
}) {
  const previewedOnce = result !== null && result.dryRun;
  const livePushed = result !== null && !result.dryRun;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-bg/70 backdrop-blur">
      <div className="w-full max-w-[520px] rounded-xl border border-line bg-surface p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="m-0 flex items-center gap-2 text-lg font-bold text-fg">
              <Wrench size={16} className="text-violet" />
              Remediate compliance drift
            </h2>
            <p className="mt-1 text-sm text-fg-dim">
              Push <b>{processName}</b> baseline to network{" "}
              <code className="font-mono text-[12px]">{networkId}</code>. This
              replaces the network's current config for that resource.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-fg-mute hover:bg-surface-2 hover:text-fg"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <div>
            <div className="text-fg-mute">Preset</div>
            <div className="font-mono text-fg">{presetSlug}</div>
          </div>
          <div>
            <div className="text-fg-mute">Drift to fix</div>
            <div className="font-semibold text-rose">
              {driftCount} pointer{driftCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {error ? (
          <output
            className="mt-3 flex items-start gap-2 rounded-md border border-rose/30 bg-rose/10 px-3 py-2 text-[12px] text-rose"
          >
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </output>
        ) : null}

        {result ? (
          <output
            className={cn(
              "mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-[12px]",
              result.dryRun
                ? "border-violet/30 bg-aurora-soft text-violet"
                : "border-mint/30 bg-mint/10 text-mint",
            )}
          >
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            <span>
              {result.dryRun ? "Dry-run queued" : "Live remediation queued"} as
              run{" "}
              <Link to={`/workflows`} className="font-mono underline">
                {result.id}
              </Link>
              .
              {result.dryRun
                ? " Review it on /workflows; if it looks right, push live below."
                : ""}
            </span>
          </output>
        ) : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onPreview}
            disabled={status === "loading"}
            title="Queue a dry-run preview — no upstream write"
          >
            {status === "loading" && !previewedOnce ? (
              <RefreshCw size={11} className="animate-spin" />
            ) : (
              <Play size={11} />
            )}
            Preview (dry-run)
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onLive}
            disabled={status === "loading" || !previewedOnce || livePushed}
            title={
              !previewedOnce
                ? "Preview first to see what will be pushed"
                : "Push live — subject to network allowlist"
            }
            className={cn(
              previewedOnce && !livePushed
                ? "border border-rose/40 bg-rose/10 text-rose hover:bg-rose/20"
                : "",
            )}
          >
            {status === "loading" && previewedOnce ? (
              <RefreshCw size={11} className="animate-spin" />
            ) : (
              <Wrench size={11} />
            )}
            Push live
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Inline tag editor for the active scan. Renders each tag as a chip
 *  with an X to remove; an input at the right takes a new tag on Enter.
 *  Mutations call onChange(nextList) — the store handles the PATCH. */
function ScanTagEditor({
  scanId,
  tags,
  onChange,
}: {
  scanId: string;
  tags: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const commit = () => {
    const t = draft.trim().slice(0, 32);
    if (!t) return;
    if (tags.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...tags, t]);
    setDraft("");
  };
  return (
    <div className="flex w-full flex-wrap items-center gap-1.5 border-t border-line pt-3">
      <Tag size={11} className="shrink-0 text-fg-mute" />
      <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
        Tags
      </span>
      {tags.length === 0 ? (
        <span className="text-[11.5px] text-fg-mute italic">
          none yet — label this scan to find it later
        </span>
      ) : null}
      {tags.map((t) => (
        <span
          key={t}
          className="inline-flex items-center gap-1 rounded-full border border-violet/40 bg-aurora-soft px-2 py-0.5 text-[10.5px] font-semibold text-violet"
        >
          {t}
          <button
            type="button"
            onClick={() => onChange(tags.filter((x) => x !== t))}
            className="rounded-full text-violet/70 hover:text-violet"
            title={`Remove '${t}'`}
            aria-label={`Remove tag ${t}`}
          >
            <X size={9} />
          </button>
        </span>
      ))}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        onBlur={commit}
        aria-label={`Add tag for scan ${scanId}`}
        placeholder="add tag…"
        maxLength={32}
        title={`Edit tags for ${scanId}`}
        className="ml-1 h-6 w-[140px] rounded-md border border-line bg-surface px-2 text-[11.5px] text-fg focus:border-violet focus:outline-none"
      />
    </div>
  );
}

/** One clickable row in the recent-scans rail. Shows preset+network +
 *  drift-count + relative timestamp. When a prior scan for the same
 *  (network, preset) exists, also renders a "Compare to previous" link
 *  that drills into the per-pointer diff page. */
function ScanRailRow({
  scan,
  prevScanId,
  onLoad,
}: {
  scan: ComplianceScanSummary;
  prevScanId: string | null;
  onLoad: () => void;
}) {
  const drift = scan.summary["non-compliant"] ?? 0;
  const verdictTone =
    drift > 0 ? "border-rose/30 bg-rose/5" : "border-mint/30 bg-mint/5";
  const sourceTag =
    scan.source === "scheduled_probe"
      ? "probe"
      : scan.source === "agent"
        ? "agent"
        : "manual";
  // Baseline-tagged scans get a subtle violet left bar so they're easy
  // to spot when scrolling. Compare-to-baseline (drill-in button) keys
  // off the same tag.
  const isBaseline = (scan.tags ?? []).includes("baseline");
  return (
    <li
      className={cn(
        "rounded-md border",
        verdictTone,
        isBaseline && "border-l-[3px] border-l-violet/70",
      )}
    >
      <button
        type="button"
        onClick={onLoad}
        className="flex w-full flex-col gap-0.5 px-2.5 py-2 text-left transition-colors hover:brightness-110"
      >
        <div className="flex items-center gap-2">
          {drift > 0 ? (
            <ShieldAlert size={11} className="shrink-0 text-rose" />
          ) : (
            <ShieldCheck size={11} className="shrink-0 text-mint" />
          )}
          <span className="truncate text-[12px] font-semibold text-fg">
            {scan.processName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] text-fg-mute">
          <Clock size={9} />
          <span title={scan.ranAt}>{formatRelative(scan.ranAt)}</span>
          <span>·</span>
          <span>{drift > 0 ? `${drift} drift` : "clean"}</span>
          <span>·</span>
          <span>{sourceTag}</span>
        </div>
        <div className="truncate font-mono text-[10px] text-fg-dim">
          {scan.networkName ?? scan.networkId}
        </div>
        {/* User-applied tags — small chips so the rail stays scannable.
            Capped to 4 visible to keep the row a fixed height; overflow
            collapses behind a "+N" pill. */}
        {scan.tags && scan.tags.length > 0 ? (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {scan.tags.slice(0, 4).map((t) => (
              <span
                key={t}
                className="inline-flex rounded-full border border-violet/40 bg-aurora-soft px-1.5 py-0.5 text-[9px] font-semibold text-violet"
              >
                {t}
              </span>
            ))}
            {scan.tags.length > 4 ? (
              <span className="text-[9px] text-fg-mute">
                +{scan.tags.length - 4}
              </span>
            ) : null}
          </div>
        ) : null}
      </button>
      {prevScanId ? (
        <div className="border-t border-current/10 px-2.5 py-1">
          <Link
            to={`/compliance/diff/${encodeURIComponent(scan.id)}/${encodeURIComponent(prevScanId)}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 font-mono text-[10px] text-violet hover:underline"
            title="Compare this scan to the previous run for the same network + preset"
          >
            <ChevronRight size={9} />
            Compare to previous
          </Link>
        </div>
      ) : null}
    </li>
  );
}

/** Trend pill comparing the current scan's drift count to the previous
 *  scan's drift count for the same (network, preset). Negative = improved,
 *  positive = regressed, zero = unchanged. */
function DeltaBadge({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <Badge
        variant="neutral"
        size="sm"
        title="Drift count unchanged from last run"
      >
        no change
      </Badge>
    );
  }
  if (delta < 0) {
    return (
      <Badge
        variant="ok"
        size="sm"
        title="Fewer non-compliant entries than last run"
      >
        ↓ {Math.abs(delta)} fewer drift{Math.abs(delta) === 1 ? "" : "s"}
      </Badge>
    );
  }
  return (
    <Badge
      variant="err"
      size="sm"
      title="More non-compliant entries than last run"
    >
      ↑ {delta} new drift{delta === 1 ? "" : "s"}
    </Badge>
  );
}

/** Render an ISO-or-SQLite-CURRENT_TIMESTAMP string as "Xs/m/h/d ago".
 *  SQLite returns 'YYYY-MM-DD HH:MM:SS' (no T, no Z) for the default
 *  CURRENT_TIMESTAMP — patch in T+Z so Date parses it as UTC. */
function formatRelative(iso: string): string {
  const norm = iso.includes("T") ? iso : iso.replace(" ", "T") + "Z";
  const t = new Date(norm).getTime();
  if (Number.isNaN(t)) return "—";
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function KpiTile({
  label,
  value,
  tone,
  Icon,
}: {
  label: string;
  value: number;
  tone?: "ok" | "err" | "warn" | "neutral";
  Icon: typeof ShieldCheck;
}) {
  const tint =
    tone === "ok"
      ? "bg-mint/15 text-mint"
      : tone === "err"
        ? "bg-rose/15 text-rose"
        : tone === "warn"
          ? "bg-peach/15 text-peach"
          : "bg-aurora-soft text-violet";
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-3">
        <div
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-lg",
            tint,
          )}
        >
          <Icon size={15} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            {label}
          </div>
          <div className="text-xl font-extrabold text-fg">
            {value.toLocaleString()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultRow({
  result,
  networkId,
  presetSlug,
}: {
  result: ComplianceResult;
  networkId: string;
  presetSlug: string;
}) {
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const pointerHistory = useCompliance((s) => s.pointerHistory);
  const loadPointerHistory = useCompliance((s) => s.loadPointerHistory);
  const histKey = `${networkId}::${presetSlug}::${result.pointer}`;
  const histEntry = pointerHistory[histKey];
  const hasDetail =
    result.errors.length > 0 ||
    result.warnings.length > 0 ||
    result.current !== null ||
    result.desired !== null;
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-3">
        <button
          type="button"
          onClick={() => hasDetail && setOpen((v) => !v)}
          className={cn(
            "flex items-center gap-3 text-left",
            hasDetail ? "cursor-pointer" : "cursor-default",
          )}
        >
          {statusBadge(result.status)}
          <span className="flex-1 truncate font-mono text-[12.5px] text-fg">
            {result.pointer || "(root)"}
          </span>
          {result.errors[0] ? (
            <span className="hidden truncate text-[11px] text-fg-mute md:inline-block max-w-[40%]">
              {result.errors[0]}
            </span>
          ) : null}
          {hasDetail ? (
            <ChevronRight
              size={14}
              className={cn(
                "shrink-0 text-fg-mute transition-transform",
                open && "rotate-90",
              )}
            />
          ) : null}
        </button>
        {open ? (
          <div className="flex flex-col gap-2 border-t border-line pt-2 text-[11.5px]">
            {result.errors.length > 0 ? (
              <div className="rounded-md border border-rose/30 bg-rose/5 px-2.5 py-1.5">
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-rose">
                  Errors
                </div>
                <ul className="m-0 list-disc pl-5 text-rose/90">
                  {result.errors.map((e) => (
                    <li key={e} className="font-mono text-[11px]">
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {result.warnings.length > 0 ? (
              <div className="rounded-md border border-peach/30 bg-peach/5 px-2.5 py-1.5">
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-peach">
                  Warnings
                </div>
                <ul className="m-0 list-disc pl-5 text-peach/90">
                  {result.warnings.map((w) => (
                    <li key={w} className="font-mono text-[11px]">
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                  Desired
                </div>
                <pre className="m-0 max-h-[200px] overflow-auto rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-[10.5px] text-fg-dim">
                  {compact(result.desired, 5000)}
                </pre>
              </div>
              <div>
                <div className="mb-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                  Current
                </div>
                <pre className="m-0 max-h-[200px] overflow-auto rounded-md border border-line bg-surface px-2 py-1.5 font-mono text-[10.5px] text-fg-dim">
                  {compact(result.current, 5000)}
                </pre>
              </div>
            </div>
            {/* Per-pointer history — trace this exact pointer's status
                back through prior scans for the same (network, preset).
                Backed by GET /api/compliance/pointers/history; returns
                the rows where the pointer was present (skips runs where
                the preset evolved). Only available when we have a
                network id (i.e. for Meraki scans; tenant-scoped Aruba
                scans currently go through "tenant" too). */}
            {result.pointer && networkId ? (
              <div className="border-t border-line pt-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
                    Pointer history
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !showHistory;
                      setShowHistory(next);
                      if (next && (!histEntry || histEntry.status === "idle")) {
                        void loadPointerHistory({
                          networkId,
                          presetSlug,
                          pointer: result.pointer,
                        });
                      }
                    }}
                    className="rounded-md border border-line bg-surface px-2 py-0.5 text-[10.5px] text-fg-mute hover:bg-surface-2 hover:text-fg"
                  >
                    {showHistory ? "Hide" : "Trace this pointer"}
                  </button>
                </div>
                {showHistory ? (
                  <PointerHistoryPanel
                    status={histEntry?.status ?? "idle"}
                    error={histEntry?.error ?? null}
                    data={histEntry?.data ?? null}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Inline timeline of a JSON pointer's compliance status across the
 *  last N scans for the same (network, preset). Renders one row per
 *  scan; rows where the pointer was non-compliant get a rose left bar
 *  so the eye picks out drift periods. */
function PointerHistoryPanel({
  status,
  error,
  data,
}: {
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  data: import("@/api/schemas/compliance").PointerHistory | null;
}) {
  if (status === "loading") {
    return <PanelSkeleton className="mt-2" />;
  }
  if (status === "error") {
    return (
      <output
        className="mt-2 flex items-start gap-2 rounded-md border border-rose/30 bg-rose/10 px-2 py-1 text-[11px] text-rose"
      >
        <AlertTriangle size={11} className="mt-0.5 shrink-0" />
        <span>{error || "could not load history"}</span>
      </output>
    );
  }
  if (!data || data.history.length === 0) {
    return (
      <div className="mt-2 text-[11px] text-fg-mute">
        No prior scans contain this pointer.
      </div>
    );
  }
  return (
    <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
      {data.history.map((h) => {
        const bad = h.status === "non-compliant";
        return (
          <li
            key={h.scanId}
            className={cn(
              "flex items-center gap-2 rounded-md border px-2 py-1 font-mono text-[10.5px]",
              bad ? "border-rose/30 bg-rose/5" : "border-mint/30 bg-mint/5",
            )}
          >
            <span
              className={cn(
                "w-2 shrink-0 text-center",
                bad ? "text-rose" : "text-mint",
              )}
            >
              {bad ? "✗" : "✓"}
            </span>
            <span className="text-fg" title={h.ranAt}>
              {h.ranAt}
            </span>
            <span className="text-fg-mute">{h.source}</span>
            {h.tags.length > 0 ? (
              <span className="text-violet">[{h.tags.join(", ")}]</span>
            ) : null}
            <span className="ml-auto truncate text-fg-dim">
              {bad && h.errors[0] ? h.errors[0] : compact(h.current, 60)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
